import React, { useState, useEffect, useRef } from "react";
import AICaptureStore from "../../../stores/AICaptureStore";
import {
  GetAICaptureGenericFields,
  GetAICaptureFields,
  UpdateAICaptureFields,
  AddAICaptureGenericField,
  DeleteAICaptureGenericField
} from "../../../actions/AICaptureActions";
import actionTypes from "../../../actions/ActionTypes";
const AI_BRAND_OPTIONS = ["Veryfi"];
const AICapture = () => {
  const [headerFields, setHeaderFields] = useState([]);
  const [lineFields, setLineFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [showAICaptureFields, setShowAICaptureFields] = useState(false);
  const [editedField, setEditedField] = useState(null);
  const [editedAICaptureFields, setEditedAICaptureFields] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [addFieldSection, setAddFieldSection] = useState(null);
  const [newFieldName, setNewFieldName] = useState("");
  const addFieldSectionRef = useRef(null);
  const [fieldToDelete, setFieldToDelete] = useState(null);
  const fieldToDeleteRef = useRef(null);
  const editedFieldRef = useRef(null);
  useEffect(() => {
    editedFieldRef.current = editedField;
  }, [editedField]);
  useEffect(() => {
    addFieldSectionRef.current = addFieldSection;
  }, [addFieldSection]);
  useEffect(() => {
    fieldToDeleteRef.current = fieldToDelete;
  }, [fieldToDelete]);
  const tempIdRef = useRef(-1);
  const onStoreChange = () => {
    const lastChange = AICaptureStore.lastChange();
    if (lastChange === actionTypes.GetAICaptureGenericFields) {
      const fields = AICaptureStore.getAICaptureGenericFields();
      setHeaderFields(fields.filter((f) => f.header === true));
      setLineFields(fields.filter((f) => f.header !== true));
    }
    if (lastChange === actionTypes.GetAICaptureFields) {
      const models = AICaptureStore.getAICaptureFields();
      setEditedAICaptureFields(models.map((m) => ({ ...m })));
      setShowAICaptureFields(true);
      setIsDirty(false);
    }
    if (lastChange === actionTypes.UpdateAICaptureFields) {
      const saved = editedFieldRef.current;
      if (saved) {
        setHeaderFields(
          (prev) => prev.map(
            (f) => f.id === saved.id ? { ...f, genericFieldName: saved.genericFieldName } : f
          )
        );
        setLineFields(
          (prev) => prev.map(
            (f) => f.id === saved.id ? { ...f, genericFieldName: saved.genericFieldName } : f
          )
        );
      }
      setShowAICaptureFields(false);
      setSelectedField(null);
      setEditedField(null);
      editedFieldRef.current = null;
      setEditedAICaptureFields([]);
      setIsDirty(false);
      setIsSaving(false);
    }
    if (lastChange === actionTypes.AddAICaptureGenericField) {
      const added = AICaptureStore.getNewGenericField();
      if (added) {
        if (addFieldSectionRef.current === "header") {
          setHeaderFields((prev) => [...prev, added]);
        } else {
          setLineFields((prev) => [...prev, added]);
        }
      }
      setAddFieldSection(null);
      addFieldSectionRef.current = null;
      setNewFieldName("");
    }
    if (lastChange === actionTypes.DeleteAICaptureGenericField) {
      const deleted = fieldToDeleteRef.current;
      if (deleted) {
        setHeaderFields((prev) => prev.filter((f) => f.id !== deleted.id));
        setLineFields((prev) => prev.filter((f) => f.id !== deleted.id));
      }
      setFieldToDelete(null);
      fieldToDeleteRef.current = null;
    }
  };
  useEffect(() => {
    AICaptureStore.addChangeListener(onStoreChange);
    GetAICaptureGenericFields();
    return () => {
      AICaptureStore.removeChangeListener(onStoreChange);
    };
  }, []);
  const onFieldClick = (field) => {
    setSelectedField(field);
    setEditedField({ ...field });
    setIsDirty(false);
    setIsSaving(false);
    tempIdRef.current = -1;
    GetAICaptureFields(field.id);
  };
  const closePopup = () => {
    setShowAICaptureFields(false);
    setSelectedField(null);
    setEditedField(null);
    setEditedAICaptureFields([]);
    setIsDirty(false);
    setIsSaving(false);
  };
  const onGenericFieldNameChange = (e) => {
    setEditedField({ ...editedField, genericFieldName: e.target.value });
    setIsDirty(true);
  };
  const onAICaptureFIeldChange = (id, property, value) => {
    setEditedAICaptureFields(
      editedAICaptureFields.map(
        (m) => m.id === id ? { ...m, [property]: value } : m
      )
    );
    setIsDirty(true);
  };
  const onAddAICaptureField = () => {
    const usedBrands = editedAICaptureFields.map((m) => m.thirdPartyAI);
    const firstAvailable = AI_BRAND_OPTIONS.find((b) => !usedBrands.includes(b));
    if (!firstAvailable) return;
    const tempId = tempIdRef.current;
    tempIdRef.current -= 1;
    setEditedAICaptureFields((prev) => [
      ...prev,
      { id: tempId, thirdPartyAI: firstAvailable, fieldName: "", genericFieldId: editedField.id }
    ]);
    setIsDirty(true);
  };
  const onRemoveAICaptureField = (id) => {
    setEditedAICaptureFields((prev) => prev.filter((m) => m.id !== id));
    setIsDirty(true);
  };
  const getAvailableBrands = (currentModelId) => {
    const usedByOthers = editedAICaptureFields.filter((m) => m.id !== currentModelId).map((m) => m.thirdPartyAI);
    return AI_BRAND_OPTIONS.filter((b) => !usedByOthers.includes(b));
  };
  const onSave = () => {
    setIsSaving(true);
    UpdateAICaptureFields({
      genericField: editedField,
      aiCaptureFields: editedAICaptureFields.map((m) => ({
        ...m,
        id: m.id < 0 ? 0 : m.id
      }))
    });
  };
  const onAddFieldClick = (section) => {
    setAddFieldSection(section);
    setNewFieldName("");
  };
  const onConfirmAddField = () => {
    if (!newFieldName.trim()) return;
    AddAICaptureGenericField({
      id: 0,
      genericFieldName: newFieldName.trim(),
      genericType: "",
      header: addFieldSection === "header"
    });
  };
  const onDeleteFieldClick = (field, e) => {
    e.stopPropagation();
    setFieldToDelete(field);
  };
  const onConfirmDelete = () => {
    if (!fieldToDelete) return;
    DeleteAICaptureGenericField(fieldToDelete.id);
  };
  const onCancelDelete = () => {
    setFieldToDelete(null);
    fieldToDeleteRef.current = null;
  };
  const renderCard = (title, icon, fields, section) => /* @__PURE__ */ React.createElement("div", { className: "card", style: { flex: "1 1 0", minWidth: 0 } }, /* @__PURE__ */ React.createElement("div", { className: "card-header" }, /* @__PURE__ */ React.createElement("i", { className: `fa-solid ${icon}` }), " ", title, /* @__PURE__ */ React.createElement(
    "button",
    {
      className: "btn btn-outline-secondary btn-sm float-end",
      onClick: () => onAddFieldClick(section)
    },
    /* @__PURE__ */ React.createElement("i", { className: "fa-solid fa-plus" })
  )), /* @__PURE__ */ React.createElement("div", { className: "card-body p-0", style: { overflowY: "auto", maxHeight: "calc(100vh - 160px)" } }, fields.length === 0 ? /* @__PURE__ */ React.createElement("p", { className: "text-muted p-3 mb-0" }, "No fields available.") : /* @__PURE__ */ React.createElement("table", { className: "table table-hover mb-0" }, /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("th", { className: "ps-3" }, "Field Name"), /* @__PURE__ */ React.createElement("th", { style: { width: "48px" } }))), /* @__PURE__ */ React.createElement("tbody", null, fields.map((field) => /* @__PURE__ */ React.createElement(
    "tr",
    {
      key: field.id,
      onClick: () => onFieldClick(field),
      style: { cursor: "pointer" }
    },
    /* @__PURE__ */ React.createElement("td", { className: "ps-3" }, field.genericFieldName),
    /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(
      "button",
      {
        className: "btn btn-danger btn-sm btn-circle",
        onClick: (e) => onDeleteFieldClick(field, e),
        title: "Delete field"
      },
      /* @__PURE__ */ React.createElement("i", { className: "fa-solid fa-trash" })
    ))
  ))))));
  const allBrandsAssigned = editedAICaptureFields.length >= AI_BRAND_OPTIONS.length;
  return /* @__PURE__ */ React.createElement("div", { className: "d-flex gap-3 p-3", style: { width: "90%", margin: "0 auto" } }, renderCard("Header Fields", "fa-heading", headerFields, "header"), renderCard("Line Fields", "fa-list", lineFields, "lines"), showAICaptureFields && selectedField && editedField && /* @__PURE__ */ React.createElement("div", { className: "td-dialogue-container" }, /* @__PURE__ */ React.createElement("div", { className: "td-dialogue card", style: { width: "620px" } }, /* @__PURE__ */ React.createElement("div", { className: "card-header" }, /* @__PURE__ */ React.createElement("i", { className: "fa-solid fa-robot" }), " AI Field Update", /* @__PURE__ */ React.createElement("button", { className: "btn-close", onClick: closePopup })), /* @__PURE__ */ React.createElement("div", { className: "card-body" }, /* @__PURE__ */ React.createElement("div", { className: "mb-3" }, /* @__PURE__ */ React.createElement("label", { className: "form-label" }, "Generic Field Name"), /* @__PURE__ */ React.createElement(
    "input",
    {
      type: "text",
      className: "form-control form-control-sm",
      value: editedField.genericFieldName,
      onChange: onGenericFieldNameChange
    }
  )), editedAICaptureFields.length === 0 ? /* @__PURE__ */ React.createElement("p", { className: "text-muted mb-0" }, "No third party AI fields found.") : /* @__PURE__ */ React.createElement("table", { className: "table table-hover mb-0" }, /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("th", null, "AI Brand"), /* @__PURE__ */ React.createElement("th", { className: "ps-3" }, "Field Name"), /* @__PURE__ */ React.createElement("th", { style: { width: "48px" } }))), /* @__PURE__ */ React.createElement("tbody", null, editedAICaptureFields.map((model) => /* @__PURE__ */ React.createElement("tr", { key: model.id }, /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(
    "select",
    {
      className: "form-select form-select-sm",
      value: model.thirdPartyAI,
      onChange: (e) => onAICaptureFIeldChange(model.id, "thirdPartyAI", e.target.value)
    },
    getAvailableBrands(model.id).map((brand) => /* @__PURE__ */ React.createElement("option", { key: brand, value: brand }, brand))
  )), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(
    "input",
    {
      type: "text",
      className: "form-control form-control-sm",
      value: model.fieldName,
      onChange: (e) => onAICaptureFIeldChange(model.id, "fieldName", e.target.value)
    }
  )), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(
    "button",
    {
      className: "btn btn-danger btn-sm btn-circle",
      onClick: () => onRemoveAICaptureField(model.id),
      title: "Remove"
    },
    /* @__PURE__ */ React.createElement("i", { className: "fa-solid fa-trash" })
  )))))), !allBrandsAssigned && /* @__PURE__ */ React.createElement(
    "button",
    {
      className: "btn btn-outline-secondary btn-sm mt-3",
      onClick: onAddAICaptureField
    },
    /* @__PURE__ */ React.createElement("i", { className: "fa-solid fa-plus" }),
    " Add third party AI field"
  ), isDirty && !isSaving && /* @__PURE__ */ React.createElement(
    "button",
    {
      className: "btn btn-success btn-sm mt-3 ms-2",
      onClick: onSave
    },
    "Save"
  ), isSaving && /* @__PURE__ */ React.createElement("i", { className: "fa-solid fa-spinner fa-spin mt-3" })))), addFieldSection !== null && /* @__PURE__ */ React.createElement("div", { className: "td-dialogue-container" }, /* @__PURE__ */ React.createElement("div", { className: "td-dialogue card", style: { width: "400px" } }, /* @__PURE__ */ React.createElement("div", { className: "card-header" }, /* @__PURE__ */ React.createElement("i", { className: "fa-solid fa-plus" }), " ", "Add ", addFieldSection === "header" ? "Header" : "Line", " Field", /* @__PURE__ */ React.createElement(
    "button",
    {
      className: "btn-close",
      onClick: () => {
        setAddFieldSection(null);
        setNewFieldName("");
      }
    }
  )), /* @__PURE__ */ React.createElement("div", { className: "card-body" }, /* @__PURE__ */ React.createElement("label", { className: "form-label" }, "Field Name"), /* @__PURE__ */ React.createElement(
    "input",
    {
      type: "text",
      className: "form-control form-control-sm",
      value: newFieldName,
      onChange: (e) => setNewFieldName(e.target.value),
      onKeyDown: (e) => e.key === "Enter" && onConfirmAddField(),
      autoFocus: true
    }
  ), /* @__PURE__ */ React.createElement(
    "button",
    {
      className: "btn btn-success btn-sm mt-3",
      onClick: onConfirmAddField,
      disabled: !newFieldName.trim()
    },
    "Add"
  )))), fieldToDelete !== null && /* @__PURE__ */ React.createElement("div", { className: "td-dialogue-container" }, /* @__PURE__ */ React.createElement("div", { className: "td-dialogue card", style: { width: "420px" } }, /* @__PURE__ */ React.createElement("div", { className: "card-header" }, /* @__PURE__ */ React.createElement("i", { className: "fa-solid fa-triangle-exclamation" }), " Delete Field", /* @__PURE__ */ React.createElement("button", { className: "btn-close", onClick: onCancelDelete })), /* @__PURE__ */ React.createElement("div", { className: "card-body" }, /* @__PURE__ */ React.createElement("p", null, "Are you sure you want to delete", " ", /* @__PURE__ */ React.createElement("strong", null, fieldToDelete.genericFieldName), "? This will also delete all associated Third Party AI field configurations and cannot be undone."), /* @__PURE__ */ React.createElement(
    "button",
    {
      className: "btn btn-danger btn-sm",
      onClick: onConfirmDelete
    },
    "Delete"
  ), /* @__PURE__ */ React.createElement(
    "button",
    {
      className: "btn btn-secondary btn-sm ms-2",
      onClick: onCancelDelete
    },
    "Cancel"
  )))));
};
export default AICapture;
